import { useEffect, useRef, useState } from 'react';
import { publisherApi } from '../api';
import { SOCIAL_GROUPS } from '../kindSchemas';
import type { PublisherFormDraft } from '../PublisherHub';
import type { AttachmentItem, SocialHandoffPayload } from '../types';
import { PublisherGroups } from './PublisherGroups';
import { PublisherPreview } from './PublisherPreview';

interface SocialHandoffFormProps {
  onSuccess: () => void;
  lockedKind?: string;
  initialDraft?: PublisherFormDraft;
  onDraftChange?: (patch: PublisherFormDraft) => void;
}

function draftValues(draft?: PublisherFormDraft): Record<string, string> {
  const channels = draft?.channels || ['Instagram'];
  return {
    subtype: draft?.materialType || 'Публикация',
    title: draft?.title || '',
    summary: draft?.description || '',
    participants: draft?.participants || '',
    eventDate: draft?.eventDate || '',
    caption: draft?.captionDraft || '',
    channelInstagram: channels.includes('Instagram') ? '1' : '',
    channelSite: channels.includes('сайт ENU') ? '1' : '',
    channelTelegram: channels.includes('Telegram') ? '1' : '',
  };
}

export const SocialHandoffForm = ({
  onSuccess,
  lockedKind,
  initialDraft,
  onDraftChange,
}: SocialHandoffFormProps) => {
  const [values, setValues] = useState<Record<string, string>>(() => draftValues(initialDraft));
  const [hashtags, setHashtags] = useState<string[]>(initialDraft?.hashtags || []);
  const [fileMap, setFileMap] = useState<Record<string, AttachmentItem[]>>({});
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const sending = useRef(false);

  useEffect(() => {
    if (lockedKind && !values.subtype) {
      setValues((prev) => ({ ...prev, subtype: 'Публикация' }));
    }
  }, [lockedKind, values.subtype]);

  const channels = [
    values.channelInstagram === '1' ? 'Instagram' : '',
    values.channelSite === '1' ? 'сайт ENU' : '',
    values.channelTelegram === '1' ? 'Telegram' : '',
  ].filter(Boolean);

  const collectedFiles = (map = fileMap) => Object.values(map).flat();

  const emit = (nextValues: Record<string, string>, nextTags = hashtags, nextFiles = collectedFiles()) => {
    const nextChannels = [
      nextValues.channelInstagram === '1' ? 'Instagram' : '',
      nextValues.channelSite === '1' ? 'сайт ENU' : '',
      nextValues.channelTelegram === '1' ? 'Telegram' : '',
    ].filter(Boolean);
    onDraftChange?.({
      materialType: nextValues.subtype,
      title: nextValues.title,
      description: nextValues.summary,
      participants: nextValues.participants,
      eventDate: nextValues.eventDate,
      captionDraft: nextValues.caption,
      channels: nextChannels,
      hashtags: nextTags,
      attachments: nextFiles,
    });
  };

  const updateValue = (name: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      emit(next);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending.current) return;
    if (!values.title?.trim() || !values.summary?.trim() || channels.length === 0) return;
    sending.current = true;
    setLoading(true);
    setSubmitError('');
    try {
      const payload: SocialHandoffPayload = {
        material_type: values.subtype || 'Публикация',
        title: values.title.trim(),
        description: values.summary.trim(),
        participants: values.participants || undefined,
        event_date: values.eventDate || undefined,
        caption_draft: values.caption || undefined,
        channels: channels.join(','),
        hashtags,
        files: collectedFiles(),
      };
      await publisherApi.createHandoff(payload);
      onSuccess();
    } catch {
      setSubmitError('Не удалось передать материал. Проверьте соединение и повторите попытку. Введённые данные остались в форме.');
    } finally {
      sending.current = false;
      setLoading(false);
    }
  };

  const openPreview = (event: React.FormEvent) => {
    event.preventDefault();
    if (!values.title?.trim() || !values.summary?.trim() || channels.length === 0) return;
    setSubmitError('');
    setPreviewing(true);
  };

  if (previewing) {
    return (
      <PublisherPreview
        badge="Соцсети / сайт ENU"
        accent="#ff7b65"
        title={values.title.trim()}
        summary={values.summary.trim()}
        tags={hashtags}
        attachments={collectedFiles()}
        rows={[
          ['Тип', values.subtype],
          ['Каналы', channels.join(', ')],
        ].filter((row): row is [string, string] => Boolean(row[1]))}
        confirmLabel="Передать ответственному →"
        busy={loading}
        error={submitError}
        onEdit={() => setPreviewing(false)}
        onConfirm={() => {
          void handleSubmit({ preventDefault() {} } as React.FormEvent);
        }}
      />
    );
  }

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={openPreview}>
      <div className="rg-chip-row">
        <span className="rg-chip is-amber">Передача на публикацию</span>
      </div>
      <p style={{ margin: 0, color: '#9bb7c6', fontSize: 13 }}>
        Этот маршрут создаёт передачу ответственному за соцсети / сайт ENU и не публикует запись в ACADEMIA.
      </p>
      <PublisherGroups
        groups={SOCIAL_GROUPS}
        values={values}
        onChange={updateValue}
        hashtags={hashtags}
        files={fileMap}
        onFiles={(name, next) => {
          setFileMap((prev) => {
            const map = { ...prev, [name]: next };
            emit(values, hashtags, Object.values(map).flat());
            return map;
          });
        }}
        onHashtags={(next) => {
          setHashtags(next);
          emit(values, next);
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" disabled={loading || channels.length === 0} className="rg-btn primary">
          Предпросмотр →
        </button>
      </div>
    </form>
  );
};
